import { useSyncExternalStore } from 'react'

import {
  ANALYTICS_FRAME_OWNER_ATTRIBUTE,
  ANALYTICS_OWNER_REQUEST,
  ANALYTICS_OWNER_RESPONSE
} from 'shared/services/analytics/navigation/analyticsFrames'

const OWNER_RESPONSE_TIMEOUT_MS = 1000

const readOwner = (): boolean | null => {
  if (window.parent === window) return false
  try {
    if (window.frameElement) {
      return (
        window.frameElement.getAttribute(ANALYTICS_FRAME_OWNER_ATTRIBUTE) ===
        'parent'
      )
    }
  } catch {
    /* Cross-origin parent: use the ownership handshake. */
  }
  return null
}

// One decision per document: GA (App) and the tracker must never disagree.
let owner: boolean | null | undefined
let handshakeStarted = false
const listeners = new Set<() => void>()

const setOwner = (next: boolean) => {
  if (owner === next) return
  owner = next
  listeners.forEach(listener => listener())
}

const getOwner = () => {
  owner ??= readOwner()
  return owner
}

const startHandshake = () => {
  handshakeStarted = true
  if (getOwner() !== null) return
  const onMessage = (event: MessageEvent<unknown>) => {
    if (event.source !== window.parent) return
    const data = event.data
    if (
      data &&
      typeof data === 'object' &&
      'type' in data &&
      data.type === ANALYTICS_OWNER_RESPONSE &&
      'owned' in data &&
      data.owned === true
    ) {
      window.clearTimeout(timeout)
      window.removeEventListener('message', onMessage)
      setOwner(true)
    }
  }
  // Keeps listening after the timeout: a parent that answers late still takes
  // over, rather than both tracking the app for the document's lifetime.
  window.addEventListener('message', onMessage)
  // No identity, route or user data is included in this discovery request.
  window.parent.postMessage({ type: ANALYTICS_OWNER_REQUEST }, '*')
  const timeout = window.setTimeout(
    () => setOwner(false),
    OWNER_RESPONSE_TIMEOUT_MS
  )
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  if (!handshakeStarted) startHandshake()
  return () => {
    listeners.delete(listener)
  }
}

export const useAnalyticsParentOwner = () =>
  useSyncExternalStore(subscribe, getOwner)
